/**
 * What the venue map draws, and how it frames it (BON-538).
 *
 * Plain functions over a Mapbox map, kept out of the component so the React
 * side is a container and a token check — see `useVenueMap`.
 */
import {
  LngLatBounds,
  type ExpressionSpecification,
  type Map as MapboxMap,
  type MapLayerMouseEvent,
} from 'mapbox-gl';

import type { VenueRosterRow } from './venues-roster-api';

/** The lower 48, for when there is nothing to fit to. */
export const FALLBACK_VIEW = { center: [-98.5, 39.8] as [number, number], zoom: 3.2 };

/** Room around the pins so none sits under the card's edge or its controls. */
const FIT_PADDING = { top: 36, bottom: 28, left: 28, right: 28 };

/** A lone venue has no extent to fit, so fitBounds would zoom to the building. */
const SINGLE_VENUE_ZOOM = 10;

const DOT_RADIUS = 5.5;
/** Big enough to find at a glance in a cluster, not so big it hides neighbours. */
const DOT_RADIUS_HOVERED = 9;

export const DOTS_LAYER = 'venue-dots';
export const VENUES_SOURCE = 'venues';

export type ColorOf = (region: VenueRosterRow['region']) => string;

export const plottable = (venues: readonly VenueRosterRow[]): VenueRosterRow[] =>
  venues.filter((venue) => venue.latitude !== null && venue.longitude !== null);

export function toGeoJSON(
  venues: readonly VenueRosterRow[],
  colorOf: ColorOf,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: plottable(venues).map((venue) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [venue.longitude as number, venue.latitude as number],
      },
      properties: { id: venue.id, name: venue.name, color: colorOf(venue.region) },
    })),
  };
}

/** Pin size as a paint expression, so only the hovered feature grows. */
export const radiusFor = (hoveredId: string | null): ExpressionSpecification | number =>
  hoveredId
    ? ['case', ['==', ['get', 'id'], hoveredId], DOT_RADIUS_HOVERED, DOT_RADIUS]
    : DOT_RADIUS;

/** Frame the pins currently on the map. */
export function fit(map: MapboxMap, venues: readonly VenueRosterRow[]): void {
  const shown = plottable(venues);
  if (shown.length === 0) {
    map.easeTo({ ...FALLBACK_VIEW, duration: 400 });
    return;
  }
  if (shown.length === 1) {
    map.easeTo({
      center: [shown[0].longitude as number, shown[0].latitude as number],
      zoom: SINGLE_VENUE_ZOOM,
      duration: 400,
    });
    return;
  }
  const bounds = new LngLatBounds();
  for (const venue of shown) {
    bounds.extend([venue.longitude as number, venue.latitude as number]);
  }
  map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: 12, duration: 400 });
}

export function installDots(map: MapboxMap, data: GeoJSON.FeatureCollection): void {
  map.addSource(VENUES_SOURCE, { type: 'geojson', data });
  map.addLayer({
    id: DOTS_LAYER,
    type: 'circle',
    source: VENUES_SOURCE,
    paint: {
      'circle-radius': DOT_RADIUS,
      'circle-color': ['get', 'color'],
      'circle-stroke-width': 1.5,
      'circle-stroke-color': '#fff',
    },
  });
}

export interface DotHandlers {
  onHoverVenue?: (id: string | null) => void;
  onSelectVenue?: (id: string) => void;
}

/** Hover and click on the pins. `handlers` is read through a ref by the caller,
 *  so these stay bound to the LATEST callbacks rather than the first ones. */
export function bindDotEvents(map: MapboxMap, handlers: () => DotHandlers): void {
  const idAt = (event: MapLayerMouseEvent): string | null =>
    (event.features?.[0]?.properties?.id as string | undefined) ?? null;

  map.on('mousemove', DOTS_LAYER, (event) => {
    map.getCanvas().style.cursor = 'pointer';
    handlers().onHoverVenue?.(idAt(event));
  });
  map.on('mouseleave', DOTS_LAYER, () => {
    map.getCanvas().style.cursor = '';
    handlers().onHoverVenue?.(null);
  });
  map.on('click', DOTS_LAYER, (event) => {
    const id = idAt(event);
    if (id) handlers().onSelectVenue?.(id);
  });
}
